import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import LocalMallOutlinedIcon from '@mui/icons-material/LocalMallOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import { useAppContext } from '../../contexts/AppContext';
import { useNavigate } from 'react-router';
import '../Customer/CustomerScreens.scss';

export default function Cart() {
  const navigate = useNavigate();
  const { cartItems, addToCart, decreaseFromCart, removeFromCart } = useAppContext();

  const subtotal = useMemo(
    () =>
      cartItems.reduce(
        (total, item) => total + Number(item.price || 0) * Number(item.quantity || 1),
        0
      ),
    [cartItems]
  );

  const discount = subtotal > 0 ? Math.min(25, Math.round(subtotal * 0.08)) : 0;
  const total = subtotal - discount;

  return (
    <Box className='customer-screen'>
      <Box className='customer-shell'>
        <Box className='customer-topbar'>
          <IconButton className='customer-iconButton' onClick={() => navigate(-1)}>
            <ArrowBackRoundedIcon />
          </IconButton>
          <Typography className='customer-topbarTitle'>My Cart</Typography>
          <IconButton className='customer-iconButton'>
            <LocalMallOutlinedIcon />
          </IconButton>
        </Box>

        <Box className='customer-stack'>
          {cartItems.length ? (
            cartItems.map((item) => (
              <Paper key={item.id} className='customer-card' sx={{ p: 1.25 }}>
                <Box className='customer-cartItem'>
                  <Box
                    component='img'
                    src={item.image}
                    alt={item.name}
                    className='customer-cartThumb'
                  />
                  <Box className='customer-cartContent'>
                    <Box>
                      <Typography className='customer-cartName'>{item.name}</Typography>
                      <Typography className='customer-muted' sx={{ fontSize: '0.86rem', mb: 1 }}>
                        Size: {item.size || 'Standard'}
                      </Typography>
                    </Box>

                    <Box className='customer-cartFooter'>
                      <Typography className='customer-price'>
                        Rs. {Number(item.price || 0) * Number(item.quantity || 1)}
                      </Typography>

                      <Box className='customer-cartControls'>
                        <Box className='customer-qtyWrap'>
                          <IconButton
                            className='customer-miniButton customer-miniButton--soft'
                            onClick={() => decreaseFromCart(item.id)}
                          >
                            <RemoveRoundedIcon fontSize='small' />
                          </IconButton>
                          <Typography
                            sx={{ fontWeight: 700, minWidth: 10, textAlign: 'center' }}
                          >
                            {item.quantity || 1}
                          </Typography>
                          <IconButton
                            className='customer-miniButton customer-miniButton--primary'
                            onClick={() => addToCart(item)}
                          >
                            <AddRoundedIcon fontSize='small' />
                          </IconButton>
                        </Box>
                        <IconButton
                          className='customer-deleteButton'
                          onClick={() => removeFromCart(item.id)}
                        >
                          <DeleteOutlineRoundedIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            ))
          ) : (
            <Paper className='customer-card' sx={{ p: 3, textAlign: 'center' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', mb: 1 }}>
                Your cart is empty
              </Typography>
              <Typography className='customer-muted' sx={{ mb: 2 }}>
                Add your favorite items to continue shopping and checkout faster.
              </Typography>
              <Button
                className='customer-actionButton customer-actionButton--primary'
                onClick={() => navigate('/')}
              >
                Continue shopping
              </Button>
            </Paper>
          )}

          <Paper className='customer-card' sx={{ p: 2 }}>
            <Box className='customer-couponRow'>
              <TextField fullWidth placeholder='Enter Discount Code' />
              <Button className='customer-actionButton customer-actionButton--soft'>
                Apply
              </Button>
            </Box>

            <Box className='customer-stack'>
              <Box className='customer-summaryRow'>
                <Typography className='customer-muted'>Sub total</Typography>
                <Typography sx={{ fontWeight: 700 }}>Rs. {subtotal}</Typography>
              </Box>
              <Box className='customer-summaryRow'>
                <Typography className='customer-muted'>Discount</Typography>
                <Typography sx={{ fontWeight: 700 }}>Rs. {discount}</Typography>
              </Box>
              <Divider />
              <Box className='customer-summaryRow'>
                <Typography sx={{ fontWeight: 700 }}>Total</Typography>
                <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>Rs. {total}</Typography>
              </Box>
            </Box>
          </Paper>

          <Button
            className='customer-actionButton customer-actionButton--primary'
            onClick={() => navigate('/checkout')}
            disabled={!cartItems.length}
          >
            Checkout
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
